/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: Prop types for checking props type.
import Moment from 'moment' // Npm: Moment.js library for date and time formatting.
import _ from 'underscore' // Npm: utility module.
import { HiArrowPath, HiPlusCircle } from 'react-icons/hi2' // Npm: React icons.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import LBRVendorAccountAttachRoutePlan from 'components/LBRVendorAccountAttachRoutePlan'
import LBRRoutePlanUpsert from 'components/LBRRoutePlanUpsert'
import LBRRoutePlanDelete from 'components/LBRRoutePlanDelete'
import LBRRoutePlanDropDownOptions from 'components/LBRRoutePlanDropDownOptions'
import { MemoizedInput } from 'components/MemoizedInput'

/*
 * GRAPHS
 */
import LBRRoutePlanReadQuery from './__query__/index.lbrroutePlan.read.query'

/*
 * STYLES
 */
import { buttonStyle } from './index.style'

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {

  // Local variable.
  let _rows

  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })

  // Debounced search states
  const [mccNameToSearch, setMccNameToSearch] = React.useState('')
  const [mncNameToSearch, setMncNameToSearch] = React.useState('')
  const [countryNameToSearch, setCountryNameToSearch] = React.useState('')

  const { 'isOpen': isLBRRoutePlanCreateOpen, 'onOpen': onLBRRoutePlanCreateOpen, 'onClose': onLBRRoutePlanCreateClose } = useDisclosure()
  const { 'isOpen': isLBRRoutePlanUpdateOpen, 'onOpen': onLBRRoutePlanUpdateOpen, 'onClose': onLBRRoutePlanUpdateClose } = useDisclosure()
  const { 'isOpen': isLBRRoutePlanDeleteOpen, 'onOpen': onLBRRoutePlanDeleteOpen, 'onClose': onDeleteModalClose } = useDisclosure()
  const { 'isOpen': isVendorAccountAttachRoutePlanOpen, 'onOpen': onVendorAccountAttachRoutePlanOpen, 'onClose': onVendorAccountAttachRoutePlanClose } = useDisclosure()
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(current => {
      const nextPaginationModel = { ...current, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])

  // Debounce handlers
  const _onMccNameToSearchInputChange = React.useRef(_.debounce((e) => {
    _ResetPagination()
    setMccNameToSearch(e.target.value)
  }, 800)).current
  const _onMncNameToSearchInputChange = React.useRef(_.debounce((e) => {
    _ResetPagination()
    setMncNameToSearch(e.target.value)
  }, 800)).current
  const _onCountryNameToSearchInputChange = React.useRef(_.debounce((e) => {
    _ResetPagination()
    setCountryNameToSearch(e.target.value)
  }, 800)).current

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onMccNameToSearchInputChange.cancel?.()
      _onMncNameToSearchInputChange.cancel?.()
      _onCountryNameToSearchInputChange.cancel?.()
    }
  }, [])

  const _QueryLBRRoutePlanReadVariables = React.useMemo(() => ({
    'routeId': passOn?.routeId,
    'displayName': passOn?.routeId?.displayName,
    mccNameToSearch,
    mncNameToSearch,
    countryNameToSearch,
    'skip': paginationModel.pageSize * paginationModel.page,
    'take': paginationModel.pageSize
  }), [countryNameToSearch, mccNameToSearch, mncNameToSearch, paginationModel.page, paginationModel.pageSize, passOn?.routeId])

  const _QueryLBRRoutePlanRead = useQuery(LBRRoutePlanReadQuery, {
    'variables': _QueryLBRRoutePlanReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
  })

  const { refetch } = _QueryLBRRoutePlanRead

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])

  const _QueryLBRRoutePlanReadRowCount = _.first(_QueryLBRRoutePlanRead?.data?.LBRRoutePlanRead)?._totalCount
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryLBRRoutePlanReadRowCount && null !== _QueryLBRRoutePlanReadRowCount) _rowCountRef.current = _QueryLBRRoutePlanReadRowCount

    return _rowCountRef.current
  }, [_QueryLBRRoutePlanReadRowCount])

  const _tableHeaderHeightRef = React.useRef(0)
  const _isFirstLoadCompleted = React.useRef(false)
  const _isCurrentViewMobile = useBreakpointValue(
    { 'base': true, 'md': false },
    { 'fallback': 'md' }
  ) ?? false

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryLBRRoutePlanRead.data) {
      // Reset states.
      setSelectedIds([])
      setSelectAll(false)
    }
  }, [
    isLBRRoutePlanCreateOpen,
    isLBRRoutePlanUpdateOpen,
    isLBRRoutePlanDeleteOpen,
    isVendorAccountAttachRoutePlanOpen,
    _QueryLBRRoutePlanRead.data
  ])

  // Object assignment.
  const _HandleCheckboxChange = id => {
    // Update state.
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const _HandleSelectAll = () => {
    // Update state.
    setSelectAll(!selectAll)
    setSelectedIds(selectAll ? [] : _QueryLBRRoutePlanRead?.data?.LBRRoutePlanRead?.map(item => item.id) || [])
  }

  // Data assignment - Track if first load completed with valid data
  if (!_QueryLBRRoutePlanRead.loading && 0 < _QueryLBRRoutePlanRead.data?.LBRRoutePlanRead?.length) {
    _isFirstLoadCompleted.current = true
  }

  if (0 === _QueryLBRRoutePlanRead.data?.LBRRoutePlanRead?.length ||
    (0 < _QueryLBRRoutePlanRead.data?.LBRRoutePlanRead?.length &&
      (!_.every(_.pluck(_QueryLBRRoutePlanRead.data?.LBRRoutePlanRead, 'status'), i => _successFlags?.includes(i)) ||
        _.some(_.pluck(_QueryLBRRoutePlanRead.data?.LBRRoutePlanRead, 'status'), i => 'LBR_ROUTE_PLAN_NOT_FOUND' === i)))) {
    _isFirstLoadCompleted.current = false
  }

  // Update rows.
  _rows = _QueryLBRRoutePlanRead.data?.LBRRoutePlanRead

  // Columns definition
  const columns = React.useMemo(() => [
    {
      field: 'checkbox',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox bg='gray.300' onChange={_HandleSelectAll} isChecked={selectAll} />
      ),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Checkbox
            bg='gray.300'
            isChecked={selectedIds.includes(row.id)}
            onChange={() => _HandleCheckboxChange(row.id)}
          />
        </Box>
      )
    },

    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'routeName',
      'headerName': 'LBR Name',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'countryName',
      'headerName': 'Country Name',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'mcc',
      'headerName': 'Mcc',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150
    },
    {
      'field': 'mnc',
      'headerName': 'Mnc',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150
    },

    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 180
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 180
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <LBRRoutePlanDropDownOptions
          routePlanIds={[...selectedIds, row.id]}
          routePlanId={row.id}
          routeId={row.routeId}
          onRoutePlanUpdate={onLBRRoutePlanUpdateOpen}
          onRoutePlanDelete={onLBRRoutePlanDeleteOpen}
          onVendorAccountAttachRoutePlanOpen={onVendorAccountAttachRoutePlanOpen}
          selectedIds={selectedIds}
        />
      )
    }
  ], [selectAll, selectedIds])

  // Rows mapping - with successFlags validation
  const rows = React.useMemo(() =>
    _.compact(
      _rows?.map((item, index) =>
        _successFlags.includes(item?.status)
          ? {
            'id': item?.id ?? `lbr-route-plan-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
            'routeId': item?.LBRRoute?.id,
            'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
            'routeName': item?.LBRRoute?.displayName ?? '-',
            'countryName': item?.Mcc?.countryName ?? '-',
            'mcc': item?.Mcc?.mcc ?? '-',
            'mnc': item?.Mnc?.mnc ?? '-',
            'createdAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
            'updatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD')
          }
          : null
      )
    ) || [],
    [_rows, paginationModel.page, paginationModel.pageSize]
  )

  // Return component.
  return (
    <>
      <Flex className="routePlan base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          height={_isCurrentViewMobile ? "500px" : "100%"}
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          {/* Header Section with Buttons */}
          <Flex pb="0px" justify="space-between" alignItems="center">
            <Flex display="flex" flexDir="row" gap="12px">
              <Button
                onClick={onLBRRoutePlanCreateOpen}
                leftIcon={<HiPlusCircle />}
                style={buttonStyle}
              >
                New LBR Route Plan
              </Button>
            </Flex>
            <Flex gap="12px">
              <Tooltip label="Refresh routes." fontSize="sm">
                <Button
                  borderRadius="8px"
                  fontWeight="450"
                  minW="20px"
                  backgroundColor="purple.200"
                  color="purple"
                  _hover={{ bg: "purple.800", color: "white" }}
                  leftIcon={<HiArrowPath />}
                  isDisabled={isRefreshing}
                  onClick={_HandleRefresh}
                >
                  Refresh
                </Button>
              </Tooltip>
              <DownloadToExcel
                cellsData={
                  0 === _QueryLBRRoutePlanRead.data?.LBRRoutePlanRead?.length ||
                  !_.every(
                    _.pluck(
                      _QueryLBRRoutePlanRead.data?.LBRRoutePlanRead,
                      "status",
                    ),
                    (i) => _successFlags?.includes(i),
                  )
                    ? []
                    : _rows?.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        "LBR Name": item?.LBRRoute?.displayName ?? "-",
                        "Country Name": item?.Mcc?.countryName ?? "-",
                        Mcc: item?.Mcc?.mcc ?? "-",
                        Mnc: item?.Mnc?.mnc ?? "-",
                        CreatedAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
                      })) || []
                }
                headersData={[
                  "S.No.",
                  "LBR Name",
                  // 'Vendor Account',
                  "Mcc",
                  "Mnc",
                  "Country Name",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/* Filter Section */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="10px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Box
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Country Name
              </Text>
              <MemoizedInput
                placeholder="Search by Country"
                className="filter searchInput"
                defaultValue={countryNameToSearch}
                onChange={_onCountryNameToSearchInputChange}
              />
            </Box>
            <Box
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mcc
              </Text>
              <MemoizedInput
                placeholder="Search by Mcc"
                className="filter searchInput"
                defaultValue={mccNameToSearch}
                onChange={_onMccNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mnc
              </Text>
              <MemoizedInput
                placeholder="Search by Mnc"
                className="filter searchInput"
                defaultValue={mncNameToSearch}
                onChange={_onMncNameToSearchInputChange}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid */}
          <Box
            sx={{
              height: _isCurrentViewMobile
                ? "calc(100vh - 200px)"
                : "calc(100vh - 220px)",
              width: "100%",
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={48}
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={_HandlePaginationModelChange}
              pageSizeOptions={[10, 20, 50, 100]}
              slots={{
                loadingOverlay: () => (
                  <TableSpinner
                    isLoading={true}
                    isEmpty={false}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noResultsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noRowsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              loading={_QueryLBRRoutePlanRead?.loading}
            />
          </Box>
        </Flex>

        {/* Modals */}
        <Modal
          size="3xl"
          title="LBR Route Plan Create"
          isOpen={isLBRRoutePlanCreateOpen}
          onClose={onLBRRoutePlanCreateClose}
          isCentered={false}
        >
          <LBRRoutePlanUpsert
            isCreateOnly={true}
            isOpen={isLBRRoutePlanCreateOpen}
            onClose={onLBRRoutePlanCreateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="3xl"
          title="LBR Route Plan Update"
          isOpen={isLBRRoutePlanUpdateOpen}
          onClose={onLBRRoutePlanUpdateClose}
          isCentered={false}
        >
          <LBRRoutePlanUpsert
            isOpen={isLBRRoutePlanUpdateOpen}
            onClose={onLBRRoutePlanUpdateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="LBR Route Plan Delete"
          isOpen={isLBRRoutePlanDeleteOpen}
          onClose={onDeleteModalClose}
        >
          <LBRRoutePlanDelete refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Attach Vendor Account"
          isOpen={isVendorAccountAttachRoutePlanOpen}
          onClose={onVendorAccountAttachRoutePlanClose}
        >
          <LBRVendorAccountAttachRoutePlan refetch={refetch} />
        </Modal>
      </Flex>
    </>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}

/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
